// src/components/home/HeroSection.jsx
import {
  CheckCircle,
  ArrowRight,
  Lock,
  Sparkles,
  Briefcase,
  User,
  BarChart,
  Clock,
  DollarSign,
  Users,
} from 'lucide-react';
import { motion } from "framer-motion";
import Button from "./ui/Button";

// Contenedor: arranca visible para no bloquear el LCP del hero.
// Sólo se usa para orquestar el staggerChildren del texto.
const containerVariants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1, transition: { staggerChildren: 0.18 } },
};

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.55, ease: "easeOut" } },
};

export default function HeroSection() {
  const handleScrollToPricing = (e) => {
    e?.preventDefault?.();
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  const features = [
    { label: "Reservas online 24/7", tier: "free", highlight: true },
    { label: "Enlace único de reserva", tier: "free", highlight: true },
    { label: "Recordatorios por email", tier: "free", highlight: true },
    { label: "Pagos con Mercado Pago", tier: "premium" },
    { label: "Recordatorios por WhatsApp", tier: "premium" },
    { label: "Panel para profesionales", tier: "free" },
  ];

  return (
    <>
      <section
        className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950 text-white"
        aria-labelledby="hero-title"
      >
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16 md:py-20 min-h-[70svh]"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="md:grid md:grid-cols-12 md:items-center md:gap-8">
            <div className="md:col-span-7 text-center md:text-left">
              <motion.div
                className="inline-flex items-center gap-2 rounded-full border border-indigo-400/30 bg-indigo-400/10 px-4 py-1.5 text-xs sm:text-sm text-indigo-100 mb-4 font-medium"
                variants={itemVariants}
              >
                <Sparkles className="h-4 w-4 text-indigo-300" aria-hidden="true" />
                Plataforma líder para profesionales en Uruguay
              </motion.div>

              <motion.h1
                id="hero-title"
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight tracking-tight text-balance"
                variants={itemVariants}
              >
                Menos llamadas, <span className="text-indigo-300 drop-shadow">más reservas</span>.{" "}
                <span className="text-white">Tu agenda trabaja 24/7.</span>
              </motion.h1>

              <motion.p
                className="mt-4 text-lg sm:text-xl text-indigo-100 max-w-2xl mx-auto md:mx-0 text-pretty"
                variants={itemVariants}
              >
                <span className="font-semibold text-indigo-300">Para profesionales:</span> Publicá tu disponibilidad, recibí reservas automáticas y gestioná tu agenda desde un panel completo.{" "}
                <span className="text-indigo-200">Para clientes:</span> Reservá turnos con tus profesionales favoritos de forma rápida y segura.
              </motion.p>

              {/* Heading visualmente oculto para mantener la jerarquía h1 → h2 → h3 antes de las cards */}
              <h2 className="sr-only">Elegí cómo querés usar AgendateHoy</h2>

              <motion.div
                className="mt-8 flex flex-col gap-6 justify-center md:justify-start"
                variants={itemVariants}
              >
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Card Profesional — primaria */}
                  <motion.div
                    className="group relative flex-1 bg-gradient-to-br from-indigo-400/10 via-indigo-500/5 to-transparent border-2 border-indigo-400/30 rounded-xl p-5 hover:border-indigo-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/20"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-indigo-400/20 rounded-lg">
                        <Briefcase className="h-6 w-6 text-indigo-300" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white">Soy Profesional</h3>
                          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-400/20 text-indigo-200 px-2 py-0.5 text-xs font-semibold border border-indigo-400/30">
                            <Sparkles className="h-3 w-3" aria-hidden="true" />
                            Plan Premium disponible
                          </span>
                        </div>
                        <p className="text-sm text-indigo-100 leading-relaxed">
                          Gestiona tu agenda, recibe reservas automáticas y haz crecer tu negocio.
                          <span className="block mt-1.5 font-semibold text-indigo-200">
                            Ideal para: médicos, peluqueros, masajistas, entrenadores y más.
                          </span>
                          <span className="block mt-2 text-xs text-indigo-200">
                            Plan Básico gratuito + funciones Premium con pagos online, recordatorios por WhatsApp y más.
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3 mb-4">
                      <span className="inline-flex items-center gap-1 text-xs text-indigo-100 bg-white/5 px-2 py-1 rounded-md">
                        <BarChart className="h-3 w-3" aria-hidden="true" />
                        Estadísticas
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-indigo-100 bg-indigo-400/15 px-2 py-1 rounded-md border border-indigo-400/30">
                        <DollarSign className="h-3 w-3" aria-hidden="true" />
                        Pagos online
                        <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-indigo-100 bg-white/5 px-2 py-1 rounded-md">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        24/7 activo
                      </span>
                    </div>
                    <Button
                      to="/auth?role=profesional&mode=register"
                      variant="primary"
                      className="w-full text-base px-6 py-3.5 font-bold shadow-xl shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:scale-[1.03] transition-all duration-300"
                      aria-describedby="cta-helper"
                      data-hero-primary-cta
                    >
                      Comenzar como Profesional
                      <ArrowRight className="ml-2 h-5 w-5 inline-block group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </Button>
                  </motion.div>

                  {/* Card Cliente — secundaria */}
                  <motion.div
                    className="group relative flex-1 bg-gradient-to-br from-blue-500/5 via-blue-400/3 to-transparent border-2 border-white/10 rounded-xl p-5 hover:border-white/20 transition-all duration-300"
                    whileHover={{ scale: 1.01 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <User className="h-6 w-6 text-blue-300" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white">Soy Cliente</h3>
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 text-emerald-300 px-2 py-0.5 text-xs font-semibold border border-emerald-400/30">
                            <CheckCircle className="h-3 w-3" aria-hidden="true" />
                            100% Gratis
                          </span>
                        </div>
                        <p className="text-sm text-blue-100 leading-relaxed">
                          Reserva turnos con tus profesionales favoritos de forma rápida y sencilla.
                          <span className="block mt-1.5 font-semibold text-emerald-300">
                            Sin costo alguno. Sin tarjeta. Sin compromiso.
                          </span>
                          <span className="block mt-2 text-xs text-blue-200">
                            Crea tu cuenta gratis y accede a todos los profesionales disponibles.
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3 mb-4">
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-300 bg-emerald-400/15 px-2 py-1 rounded-md border border-emerald-400/30">
                        <CheckCircle className="h-3 w-3" aria-hidden="true" />
                        Completamente gratis
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-blue-200 bg-white/5 px-2 py-1 rounded-md">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        Disponibilidad real
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-blue-200 bg-white/5 px-2 py-1 rounded-md">
                        <Users className="h-3 w-3" aria-hidden="true" />
                        Sin registro de tarjeta
                      </span>
                    </div>
                    <Button
                      to="/auth?role=cliente&mode=register"
                      variant="secondary"
                      className="w-full text-base px-6 py-3.5 font-semibold"
                      aria-label="Comenzar como Cliente"
                    >
                      Comenzar como Cliente
                      <ArrowRight className="ml-2 h-5 w-5 inline-block group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </Button>
                  </motion.div>
                </div>

                <Button
                  onClick={handleScrollToPricing}
                  variant="secondary"
                  className="w-full sm:w-auto px-6 py-3 self-center md:self-start border border-white/40 text-white bg-white/10 hover:bg-white/20"
                  aria-label="Ver planes y precios"
                >
                  Ver planes y precios
                </Button>
              </motion.div>

              <motion.div
                className="mt-3 text-sm text-indigo-100 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 justify-center md:justify-start"
                variants={itemVariants}
                id="cta-helper"
              >
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-400" aria-hidden="true" />
                  Plan Básico • Sin tarjeta
                </span>
                <span className="hidden sm:inline text-indigo-300" aria-hidden="true">•</span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-400" aria-hidden="true" />
                  Alta en 2 minutos
                </span>
                <span className="hidden sm:inline text-indigo-300" aria-hidden="true">•</span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-400" aria-hidden="true" />
                  Soporte por WhatsApp
                </span>
              </motion.div>

              <motion.ul
                className="mt-8 flex flex-wrap gap-2 justify-center md:justify-start text-xs sm:text-sm"
                variants={itemVariants}
                aria-label="Beneficios principales"
              >
                {features.map(({ label, tier, highlight }) => (
                  <li
                    key={label}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-indigo-100"
                  >
                    {label}
                    {tier === "premium" && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-indigo-400/15 text-indigo-200 px-2 py-[2px]"
                        aria-label="Funcionalidad de plan Premium"
                        title="Funcionalidad de plan Premium"
                      >
                        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                        Premium
                      </span>
                    )}
                    {tier === "free" && highlight && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-400/15 text-emerald-300 px-2 py-[2px]"
                        aria-label="Incluido en el plan Gratis"
                        title="Incluido en el plan Gratis"
                      >
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        Gratis
                      </span>
                    )}
                  </li>
                ))}
              </motion.ul>

              <motion.p
                className="mt-3 text-xs text-indigo-200"
                variants={itemVariants}
              >
                *Pagos y recordatorios por WhatsApp disponibles en Plan Premium.{" "}
                <a
                  href="#pricing"
                  onClick={handleScrollToPricing}
                  className="underline decoration-dotted underline-offset-2 cursor-pointer hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 rounded-sm"
                >
                  Ver planes
                </a>
                .
              </motion.p>
            </div>

            {/* LCP: sin motion para que pinte en el primer frame disponible */}
            <figure
              className="hidden md:flex md:col-span-5 justify-end"
              aria-hidden="true"
            >
              <picture>
                <source
                  type="image/avif"
                  srcSet="/img/agenda-hero-420.avif 1x, /img/agenda-hero-840.avif 2x"
                />
                <source
                  type="image/webp"
                  srcSet="/img/agenda-hero-420.webp 1x, /img/agenda-hero-840.webp 2x"
                />
                <img
                  src="/img/agenda-hero-420.webp"
                  alt="Vista de AgendateHoy en un dispositivo mostrando turnos y reservas"
                  width="420"
                  height="746"
                  fetchPriority="high"
                  decoding="async"
                  className="w-[82%] max-w-[420px] drop-shadow-2xl rounded-2xl"
                />
              </picture>
            </figure>
          </div>
        </motion.div>
      </section>

      {/* Imagen del dispositivo solo en mobile */}
      <section
        className="md:hidden bg-gradient-to-b from-indigo-950 to-slate-950 py-8 sm:py-12 px-4 sm:px-6 lg:px-8"
        aria-labelledby="device-preview-title"
      >
        <h2 id="device-preview-title" className="sr-only">
          Vista previa de la aplicación en un dispositivo
        </h2>

        <motion.div
          className="max-w-7xl mx-auto flex items-center justify-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <figure className="relative">
            <picture>
              <source
                type="image/avif"
                srcSet="/img/agenda-hero-420.avif 1x, /img/agenda-hero-840.avif 2x"
              />
              <source
                type="image/webp"
                srcSet="/img/agenda-hero-420.webp 1x, /img/agenda-hero-840.webp 2x"
              />
              <img
                src="/img/agenda-hero-420.webp"
                alt="Vista de AgendateHoy en un teléfono mostrando turnos y reservas"
                width="420"
                height="746"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 480px) 85vw, 420px"
                className="w-[85vw] max-w-[420px] drop-shadow-2xl rounded-2xl"
              />
            </picture>
            <figcaption className="mt-3 text-center text-sm text-indigo-100">
              Diseño pensado para móviles. Tus clientes reservan en segundos.
            </figcaption>
          </figure>
        </motion.div>
      </section>
    </>
  );
}
